import { EditAIKeysRequest, ID } from "@project/shared-types";
import React, { JSX, useState } from "react";
import { JobChangeAIKeysAPI } from "../../api/JobsAPI";
import AISvg from "../../assets/taiga-uikit/icons/ai.svg?react";
import {
  _colorText,
  _size,
  _sizeText,
  _style,
  _typeLabel,
} from "../../core/core";
import { InputTag } from "../InputTag/InputTag";
import { ModalBase } from "../Modal/ModalBase";
import { Label } from "../texts/label";
import { Text } from "../texts/text";
import "./KeyWordBlock.css";

interface KeyWordBlockProps {
  job_id: ID;
  text: string;
  keywords: string[];
  setKeywords: React.Dispatch<React.SetStateAction<string[]>>;
  is_edit?: boolean;
}

export const KeyWordBlock: React.FC<KeyWordBlockProps> = ({
  job_id,
  text,
  keywords,
  setKeywords,
  is_edit = false,
}) => {
  const [value, setValue] = useState<string>("");
  const [active, setActive] = useState<boolean>(false);

  const fetchEditAI = async (): Promise<void> => {
    try {
      const data = { keywords };

      const response = await JobChangeAIKeysAPI({
        job_id: job_id,
        actual_text: text,
        keywords: keywords,
      } as EditAIKeysRequest);
      if (!response) return;
      setKeywords(response.keywords);
    } catch (error) {
      console.error("Ошибка при получении ключевых слов:", error);
    }
  };

  const getModal = (): JSX.Element => (
    <ModalBase
      title="Ключевые слова"
      button_size={_size.M}
      button_style={_style.linkDefault}
      active={active}
      setActive={setActive}
      button_text="Как это работает?"
    >
      <Text size={_sizeText.mbold} color={_colorText.black}>
        Что такое ключевые слова в отзывах?
      </Text>
      <Text size={_sizeText.m} color={_colorText.black}>
        Ключевые слова - это специальные слова или фразы, описывающие ваш
        бизнес, продукты или услуги в отзывах. Они помогают выделить важные
        аспекты вашего предложения.
      </Text>
      <Text size={_sizeText.mbold} color={_colorText.black}>
        Как они влияют на рекомендации и поиск?
      </Text>
      <Text size={_sizeText.m} color={_colorText.black}>
        Ключевые слова помогают вашему бизнесу появляться выше в результатах
        поиска, улучшая видимость. Релевантные ключевые слова привлекают целевую
        аудиторию, делая ваши отзывы более полезными для потенциальных клиентов.
        Поисковые системы используют ключевые слова для определения темы
        страницы, повышая вероятность рекомендации вас.
      </Text>
      <Text size={_sizeText.mbold} color={_colorText.black}>
        Примеры использования:
      </Text>
      <Text size={_sizeText.m} color={_colorText.black}>
        Для кафе: атмосфера, кофе, обслуживание. Для автосервиса:
        профессиональный, ремонт, цена, мастер. Для интернет-магазина: доставка,
        товары, сайт.
      </Text>
      <Text size={_sizeText.mbold} color={_colorText.black}>
        Готовый пример:
      </Text>
      <Text size={_sizeText.m} color={_colorText.black}>
        Сделали отличный ремонт моего авто, мастера работают профессионально,
        сделали качественный ремонт.
      </Text>
      <Text size={_sizeText.xs} color={_colorText.neutral}>
        Мы не исследовали конкретные платформы, влияют ли ключевые слова на
        поиск и рекомендации платформы, но отзывы на сайт учитываются в поиске
        Яндекса - мы это исследовали.
      </Text>
    </ModalBase>
  );

  return (
    <>
      {is_edit ? (
        <InputTag
          leftOutside={
            <Text size={_sizeText.xs} color={_colorText.neutral}>
              Ключевые слова для текста <AISvg />
            </Text>
          }
          rightOutside={getModal()}
          placeholder="Укажите ключевые слова"
          value={value}
          setValue={setValue}
          onClickDropdown={fetchEditAI}
          tags={keywords}
          setTags={setKeywords}
          is_dropdown
        />
      ) : (
        <div className="key-words">
          <div className="key-words-h">
            <Text size={_sizeText.m} color={_colorText.neutral}>
              Ключевые слова для текста:
            </Text>
            {getModal()}
          </div>
          <div className="key-words-b">
            {keywords.length === 0 ? (
              <Text size={_sizeText.m} color={_colorText.black}>
                Настроить можно в редактирование
              </Text>
            ) : (
              <>
                {keywords.map((word, index) => (
                  <Label key={index} type={_typeLabel.neutral} size={_size.S}>
                    {word}
                  </Label>
                ))}
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
};
